import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoleName } from '@/hooks/permissions';
import { supabase } from '@/integrations/supabase/client';
import { Building2, UserCircle } from 'lucide-react';

const Onboarding = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, profile, currentCompany } = useAuth();
  const { roleName } = useUserRoleName();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // If no user, redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If user is fully set up (has profile with complete data), redirect to dashboard
  if (profile && profile.first_name && profile.last_name && currentCompany) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleProfileSetup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const firstName = (formData.get('firstName') as string)?.trim();
    const lastName = (formData.get('lastName') as string)?.trim();

    // Validation
    if (!firstName || !lastName) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: 'Please enter your first and last name',
      });
      setIsLoading(false);
      return;
    }

    try {
      // Update the user's profile with first_name and last_name
      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const { data: updatedProfile, error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
        })
        .eq('user_id', user.id)
        .select();

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`);
      }

      if (!updatedProfile || updatedProfile.length === 0) {
        throw new Error('Profile update failed - no rows affected');
      }

      toast({
        title: t('common.success'),
        description: 'Welcome! Your profile has been completed.',
      });

      // Force navigation to dashboard - don't rely on state refresh
      window.location.href = '/dashboard';

    } catch (error: any) {
      console.error('Error completing profile:', error);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || 'Failed to complete your profile',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 bg-primary rounded-full flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">Welcome to Wololo Solutions</h1>
          {currentCompany && (
            <p className="text-muted-foreground mt-2">
              You've been invited to join <span className="font-semibold">{currentCompany.name}</span>
            </p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-primary" />
              Complete Your Profile
            </CardTitle>
            <CardDescription>
              Tell us your name to finish setting up your account
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleProfileSetup} autoComplete="off">
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    required
                    placeholder="John"
                    defaultValue={profile?.first_name || ''}
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    required
                    placeholder="Doe"
                    defaultValue={profile?.last_name || ''}
                    autoComplete="family-name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="bg-muted"
                />
              </div>

              {currentCompany && (
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={currentCompany.name}
                    disabled
                    className="bg-muted"
                  />
                </div>
              )}

              {profile && roleName && (
                <div className="space-y-2">
                  <Label>Your Role</Label>
                  <Input
                    value={roleName}
                    disabled
                    className="bg-muted"
                  />
                </div>
              )}
            </CardContent>
            <CardContent>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Completing setup...' : 'Complete Setup'}
              </Button>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
